import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Zap, CheckCircle2, Sparkles, ArrowDown } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import useEnergyControlQuickScan from '@/hooks/useEnergyControlQuickScan';
import { trackFunnelEvent } from '@/utils/tracking';
import QuickScanStep1Goal from '@/components/energy-control/QuickScanStep1Goal';
import QuickScanStep2Solar from '@/components/energy-control/QuickScanStep2Solar';
import QuickScanStep3Appliances from '@/components/energy-control/QuickScanStep3Appliances';
import EnergyControlIntakeHero from '@/components/energy-control/EnergyControlIntakeHero';
import EnergyControlIntakeHeroMobile from '@/components/energy-control/EnergyControlIntakeHeroMobile';
import EnergyControlDemoExperience from '@/components/energy-control/EnergyControlDemoExperience';
import DeepDiveOptional from '@/components/energy-control/DeepDiveOptional';
import IntroPriceBanner from '@/components/energy-control/IntroPriceBanner';
import { stepProgressLabel, stepCtaLabel, earlyValuePreview } from '@/utils/quickscanProgress';

/**
 * EnergyControlIntakePage — commercial cut Feb 2026:
 *   - 3 vragen vóór demo (goal · solar · appliances)
 *   - Geen postcode / woonsituatie / techniek voor demo
 *   - Prominente €9,95 introductietarief banner bovenaan zowel scan
 *     als demo
 *   - DeepDive (optioneel post-demo) vraagt nu ook woonsituatie /
 *     huishoudgrootte / postcode + de oude technische vragen
 */
const HeaderBar = ({ stage, navigate }) => (
  <div className="container mx-auto px-6 mb-6">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2">
        <Zap className="w-6 h-6 text-emerald-400" />
        <span className="text-xl font-light text-white" style={{ fontFamily: 'Outfit' }}>
          Energy Control Plan
        </span>
      </div>
      <button
        onClick={() => navigate('/')}
        className="text-sm text-white/60 hover:text-emerald-300"
        data-testid="ec-exit-btn"
      >
        {stage === 'quickscan' ? 'Afsluiten' : 'Naar home'}
      </button>
    </div>
  </div>
);

export default function EnergyControlIntakePage() {
  const navigate = useNavigate();
  const {
    stage, currentStep, totalSteps, progress,
    answers, errors, loading,
    quickscanId, demo, deepdiveLoading,
    update, toggleAppliance,
    handleNext, handleBack, skipStep3ToDemo,
    trackModuleViewed, trackDemoCtaClicked,
    startDeepDive, submitDeepDive,
  } = useEnergyControlQuickScan();

  const containerRef = useRef(null);
  useEffect(() => {
    if (!errors || Object.keys(errors).length === 0) return;
    const t = window.setTimeout(() => {
      const root = containerRef.current || document;
      const firstError = root.querySelector('.text-destructive');
      if (firstError && typeof firstError.scrollIntoView === 'function') {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [errors]);

  const demoRef = useRef(null);
  useEffect(() => {
    if (stage === 'demo' && demoRef.current) {
      const t = window.setTimeout(() => {
        demoRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
      return () => window.clearTimeout(t);
    }
  }, [stage]);

  // Sprint B v1 (Jun 2026) — addresses T+24 leak `quickscan_started_no_step_completed`
  // (10→0 in 29h). Two surgical fixes within guardrails (no consent / pricing / Stripe):
  //   1. Auto-scroll the question card into view on Step 1 so users actually SEE the
  //      goal options after cookie-consent dismissal (mobile cookie banner can occupy
  //      30%+ of the viewport and hide the form).
  //   2. Fire a `quickscan_form_visible` engagement event the first time the question
  //      card enters the viewport, so we can distinguish "page-mounted" from "form
  //      actually seen" in the next T+48 report.
  const questionCardRef = useRef(null);
  useEffect(() => {
    if (stage !== 'quickscan' || currentStep !== 1) return;
    const t = window.setTimeout(() => {
      try {
        questionCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch { /* ignore */ }
    }, 600);
    return () => window.clearTimeout(t);
  }, [stage, currentStep]);

  useEffect(() => {
    if (stage !== 'quickscan') return;
    const el = questionCardRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let fired = false;
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !fired) {
          fired = true;
          try {
            const sid = sessionStorage.getItem('powersure_session') || '';
            const guardKey = `ps_qs_form_visible_${sid}`;
            if (!sessionStorage.getItem(guardKey)) {
              trackFunnelEvent('quickscan_form_visible', {
                step: currentStep,
                flow: 'energy_control_intake',
                meta: { step: currentStep, intersection_ratio: Math.round((e.intersectionRatio || 0) * 100) / 100 },
              });
              sessionStorage.setItem(guardKey, '1');
            }
          } catch { /* ignore */ }
        }
      }
    }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [stage, currentStep]);



  // ---------- 3-vragen Quick Scan ---------- //
  if (stage === 'quickscan') {
    return (
      <div
        className="min-h-screen py-6 sm:py-10 pb-32 sm:pb-10"
        style={{ background: 'linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }}
        data-testid="ec-quickscan-root"
      >
        <HeaderBar stage={stage} navigate={navigate} />

        <div className="container mx-auto px-6 max-w-2xl">
          {currentStep === 1 && (
            <>
              <EnergyControlIntakeHeroMobile />
              <EnergyControlIntakeHero />
            </>
          )}

          {/* Persistent price reminder for steps 2-3 (de hero zit alleen op stap 1) */}
          {currentStep > 1 && (
            <IntroPriceBanner variant="hero" testId="quickscan-intro-banner" />
          )}

          <Progress value={progress} className="h-2" data-testid="ec-progress" />
          <p className="text-sm font-medium text-emerald-300 mt-2" data-testid="ec-step-label">
            {stepProgressLabel(currentStep)}
          </p>
        </div>

        <div className="container mx-auto px-6">
          <div
            ref={(el) => { containerRef.current = el; questionCardRef.current = el; }}
            className="max-w-2xl mx-auto rounded-2xl p-6 sm:p-8 shadow-2xl border border-white/10 mt-3"
            style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.10) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
            data-testid="ec-container"
          >
            {/* Sprint B v1 (Jun 2026) — explicit one-tap hint on Step 1 above the
                goal options. Addresses 10→0 step-completion leak on mobile where
                users land on the page but the form is below hero + cookie banner. */}
            {currentStep === 1 && (
              <div
                className="mb-5 flex items-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-400/30 px-3.5 py-2.5"
                data-testid="quickscan-step1-tap-hint"
              >
                <ArrowDown className="w-4 h-4 text-emerald-300 shrink-0 animate-bounce" />
                <div className="text-sm text-white/90 leading-snug">
                  Tik op één antwoord hieronder om verder te gaan — duurt ±30 sec.
                </div>
              </div>
            )}

            {/* GO4 mini-preview: directe waarde na step 1, getoond op step 2-3. */}
            {currentStep > 1 && answers.goal && earlyValuePreview(answers.goal) && (
              <div
                className="mb-5 flex items-start gap-2 rounded-lg bg-emerald-500/10 border border-emerald-400/30 px-3.5 py-2.5"
                data-testid="quickscan-early-value-preview"
              >
                <Sparkles className="w-4 h-4 mt-0.5 text-emerald-300 shrink-0" />
                <div className="text-sm text-white/85 leading-snug">
                  <span className="font-semibold text-emerald-300">Eerste signaal:</span>{' '}
                  {earlyValuePreview(answers.goal)}
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <QuickScanStep1Goal
                value={answers.goal}
                onUpdate={(v) => update({ goal: v })}
                error={errors?.goal}
              />
            )}
            {currentStep === 2 && (
              <QuickScanStep2Solar
                value={answers.solar}
                onUpdate={(v) => update({ solar: v })}
                error={errors?.solar}
              />
            )}
            {currentStep === 3 && (
              <QuickScanStep3Appliances
                value={answers.appliances}
                onToggle={toggleAppliance}
                onSkipToDemo={skipStep3ToDemo}
              />
            )}

            <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-white/15 bg-white/5 text-white/85 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
                data-testid="ec-back-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Terug
              </button>
              <button
                onClick={handleNext}
                disabled={loading}
                className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold flex items-center gap-2 px-6 py-2.5 rounded-md disabled:opacity-50 transition-colors shadow-lg shadow-emerald-900/40"
                data-testid={currentStep === totalSteps ? 'ec-submit-btn' : 'ec-next-btn'}
              >
                {stepCtaLabel(currentStep, loading)}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <p className="text-center text-xs text-white/45 mt-3" data-testid="ec-microcopy">
              Geen technische kennis nodig · ±2 minuten · u kunt later altijd aanpassen
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Demo Experience + optionele DeepDive ---------- //
  return (
    <div className="min-h-screen py-10" style={{ background: 'linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }}>
      <HeaderBar stage={stage} navigate={navigate} />

      <div className="container mx-auto px-6">
        <div ref={demoRef} className="max-w-2xl mx-auto" data-testid="ec-demo-root">
          {/* Belofte ook bovenaan demo */}
          <IntroPriceBanner variant="hero" testId="demo-intro-banner" />

          <div
            className="mb-5 flex items-center gap-2 text-emerald-200 bg-emerald-500/10 border border-emerald-400/30 rounded-lg px-3 py-2"
            data-testid="qs-completion-banner"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-300" />
            <span className="text-sm font-medium">Klaar! Hieronder uw persoonlijke voorbeeld-overzicht.</span>
          </div>

          {(stage === 'demo' || stage === 'deepdive_done') && (
            <EnergyControlDemoExperience
              demo={demo}
              quickscanId={quickscanId}
              onModuleView={trackModuleViewed}
              onCtaClick={trackDemoCtaClicked}
              onStartDeepDive={startDeepDive}
            />
          )}

          {stage === 'deepdive' && (
            <DeepDiveOptional
              loading={deepdiveLoading}
              onSubmit={submitDeepDive}
              onCancel={() => {
                window.location.hash = '';
              }}
            />
          )}

          {stage === 'deepdive_done' && (
            <div
              className="mt-6 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-4 sm:p-5"
              data-testid="deepdive-done-banner"
            >
              <p className="text-sm text-white font-medium">
                Bedankt — uw extra antwoorden zijn opgeslagen.
              </p>
              <p className="text-xs text-white/70 mt-1">
                Powersure gebruikt deze om uw advies persoonlijker te maken zodra u Mijn Powersure start.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
